import type { CompanyAccountModelDto } from '@treasury/api/channel';
import { TmContainer } from '@treasury/presentation';
import { type TmValueChanged } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-text-field';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { defaultAchExceptionsSearchRequest } from '../data/default-ach-exceptions-search-request';
import { mapAccountToVm, mapSecCodeToVm, SecCode } from './ach-exceptions.types';

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultPostedRange: DateRange = {
    rangeType: DateRangeType.None,
};

export const tagName = 'ach-exceptions-filter-sheet';
@customElement(tagName)
export class AchExceptionsFilterSheet extends TmContainer {
    @property({ type: Object })
    public filterRequest = defaultAchExceptionsSearchRequest;

    @property({ type: Array })
    public accounts: CompanyAccountModelDto[] = [];

    @property({ type: Array })
    public secCodes: SecCode[] = [];

    @state()
    private open = false;

    @state()
    private selectedAccounts: CompanyAccountModelDto[] = [];

    @state()
    private selectedSecCodes: SecCode[] = [];

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @state()
    private postedDate: DateRange = defaultPostedRange;

    @state()
    private description = '';

    public render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <div slot="header-center">Filter ACH Exceptions</div>
            <div>
                <tm-section class="p-3">
                    <tm-multi-select
                        class="mb-3"
                        required
                        .items=${this.accounts}
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Accounts'}
                        .initial=${this.selectedAccounts}
                        .mapItem=${mapAccountToVm}
                        @value-changed=${(e: TmValueChanged<CompanyAccountModelDto[]>) => {
                            this.selectedAccounts = e.detail.value || [];
                        }}
                        label="Accounts"
                    ></tm-multi-select>
                    <tm-multi-select
                        class="mb-3"
                        required
                        .items=${this.secCodes}
                        .allowSelectAll=${true}
                        .allSelectedText=${'All SEC Codes'}
                        .initial=${this.selectedSecCodes}
                        .mapItem=${mapSecCodeToVm}
                        @value-changed=${(e: TmValueChanged<SecCode[]>) => {
                            this.selectedSecCodes = e.detail.value || [];
                        }}
                        label="SEC Codes"
                    ></tm-multi-select>
                    <tm-currency-range
                        class="mb-3"
                        label="Amount"
                        .initial=${this.amountRange}
                        @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                            const { value } = e.detail;
                            if (!value) {
                                return;
                            }
                            this.amountRange = value;
                        }}
                    ></tm-currency-range>
                    <tm-date-range
                        class="mb-3"
                        label="Posted Date"
                        .initial=${this.postedDate}
                        @value-changed=${(e: TmValueChanged<DateRange>) => {
                            const { value } = e.detail;
                            if (value) {
                                this.postedDate = value;
                            }
                        }}
                    ></tm-date-range>
                    <tm-text-field
                        label="Entry Description"
                        .initial=${this.description}
                        @value-changed=${(e: CustomEvent) => {
                            this.description = e.detail.value;
                        }}
                    ></tm-text-field>
                </tm-section>
            </div>
            <tm-footer
                class="absolute"
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        importance: 'primary',
                        disabled: !this.selectedAccounts.length || !this.selectedSecCodes.length,
                        onClick: () => {
                            this.filter();
                            this.dispatchEvent(new CustomEvent('close'));
                        },
                    },
                    {
                        text: 'Reset',
                        importance: 'secondary',
                        onClick: () => {
                            this.amountRange = { ...defaultAmountRange };
                            this.postedDate = defaultPostedRange;
                            this.description = '';
                            this.selectedAccounts = [...this.accounts];
                            this.selectedSecCodes = [...this.secCodes];
                        },
                    },
                ]}
            ></tm-footer>
        </tm-bottom-sheet>`;
    }

    public firstUpdated() {
        this.selectedAccounts = [...this.accounts];
        this.selectedSecCodes = [...this.secCodes];
        this.filter();
    }

    private filter() {
        if (this.selectedAccounts.length < 1) {
            this.selectedAccounts = [...this.accounts];
        }
        if (this.selectedSecCodes.length < 1) {
            this.selectedSecCodes = [...this.secCodes];
        }

        this.dispatchEvent(
            new CustomEvent('filter', {
                detail: {
                    accounts: [...this.accounts],
                    secCodes: [...this.secCodes],
                    amountRange: this.amountRange,
                    postedDate: this.postedDate,
                    description: this.description,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionsFilterSheet;
    }
}
